import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { getSupabase } from "@/lib/supabase-lazy";
import { claimReferral } from "@/lib/referrals.functions";
import { guardAuthAction } from "@/lib/auth-guard.functions";
import { requestEarlyAccess, syncEarlyAccess } from "@/lib/early-access.functions";
import { guardMagicLink } from "@/lib/magic-link.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Sign in — The Founders App" },
      {
        name: "description",
        content: "Sign in to The Founders App to reach your businesses, daily entries, journal and contacts.",
      },
      { property: "og:title", content: "Sign in — The Founders App" },
      { property: "og:description", content: "Sign in to your private The Founders App tracker." },
    ],
  }),
  component: AuthPage,
});

const REF_KEY = "momentum.ref";

type Mode = "signin" | "request" | "create" | "reset" | "beta";

function AuthPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [invite, setInvite] = useState<string | null>(null);
  const claim = useServerFn(claimReferral);
  const guard = useServerFn(guardAuthAction);
  const sendRequest = useServerFn(requestEarlyAccess);
  const syncCohort = useServerFn(syncEarlyAccess);
  const guardMagic = useServerFn(guardMagicLink);
  const [magicSent, setMagicSent] = useState(false);
  const [agreeFeedback, setAgreeFeedback] = useState(false);

  const sendMagicLink = async () => {
    if (!email.trim()) {
      toast.error("Enter your email first.");
      return;
    }
    setBusy(true);
    try {
      const gate = await guardMagic({ data: { email } });
      if (!gate.ok) {
        if ("silent" in gate && gate.silent) {
          setMagicSent(true);
          toast.success(gate.message);
        } else {
          toast.error(gate.message);
        }
        return;
      }
      const { error } = await (await getSupabase()).auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: false,
          emailRedirectTo: `${window.location.origin}/auth`,
        },
      });
      if (error && !/signups not allowed|user not found/i.test(error.message)) throw error;
      setMagicSent(true);
      toast.success(gate.message);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't send that link");
    } finally {
      setBusy(false);
    }
  };

  const [reqName, setReqName] = useState("");
  const [reqBusiness, setReqBusiness] = useState("");
  const [requested, setRequested] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // Router search serialisation can wrap string values in quotes (beta=%221%22),
    // so strip them before comparing.
    const betaParam = (params.get("beta") ?? "").replace(/^"|"$/g, "");
    if (betaParam === "1" || betaParam === "true") setMode("beta");
    const fromUrl = params.get("ref");
    if (fromUrl) {
      localStorage.setItem(REF_KEY, fromUrl.toUpperCase());
    }
    setInvite(localStorage.getItem(REF_KEY));
  }, []);

  useEffect(() => {
    void getSupabase().then((supabase) =>
      supabase.auth.getSession().then(({ data }) => {
        if (data.session) void navigate({ to: "/app/today" });
      }),
    );
  }, [navigate]);

  const submitRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await sendRequest({
        data: { name: reqName, email, business: reqBusiness },
      });
      if (!res.ok) {
        toast.error(res.message);
        return;
      }
      setRequested(res.message);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't send that request");
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "reset") {
        const gate = await guard({ data: { email, action: "reset" } });
        if (!gate.ok) {
          toast.error(gate.message);
          return;
        }
        const { error } = await (await getSupabase()).auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (error) throw error;
        toast.success("If that email has an account, a reset link is on its way.");
        setMode("signin");
        return;
      }
      if (mode === "beta") {
        if (!agreeFeedback) {
          toast.error("Tick the weekly feedback agreement to take a beta seat.");
          return;
        }
        const gate = await guard({ data: { email, action: "beta" } });
        if (!gate.ok) {
          toast.error(gate.message);
          return;
        }
        const { error } = await (await getSupabase()).auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/app/today`,
            data: { beta_intent: true },
          },
        });
        if (error) throw error;
        toast.success("Beta seat reserved — check your email to confirm, then sign in.");
        setMode("signin");
        return;
      }
      if (mode === "create") {
        const gate = await guard({ data: { email, action: "signup" } });
        if (!gate.ok) {
          toast.error(gate.message);
          return;
        }
        const { error } = await (await getSupabase()).auth.signUp({
          email,
          password,
          options: { emailRedirectTo: `${window.location.origin}/app/today` },
        });
        if (error) throw error;
        toast.success("Account created — check your email to confirm, then sign in.");
        setMode("signin");
        return;
      }
      const supabase = await getSupabase();
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        try {
          await syncCohort({});
        } catch {
          /* non-blocking */
        }
        // Always attempt a claim: with no local code the server falls back to
        // the pending invite stored against this email (any device).
        const code = localStorage.getItem(REF_KEY) ?? "";
        try {
          const res = await claim({ data: { code } });
          if (res.ok) toast.success("Invite applied — 90 days on us.");
        } catch {
          /* non-blocking */
        }
        localStorage.removeItem(REF_KEY);
        void navigate({ to: "/app/today" });
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't sign in");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main id="main" className="flex min-h-screen items-center justify-center px-6 py-16">
      <div className="w-full max-w-sm">
        <Link to="/" className="text-xs tracking-[0.3em] text-muted-foreground uppercase">
          The Founders App
        </Link>
        <h1 className="mt-6 font-display text-3xl">
          {mode === "signin"
            ? "Welcome back"
            : mode === "beta"
              ? "Join the free beta"
              : mode === "request"
                ? "Request early access"
                : mode === "create"
                  ? "Create your account"
                  : "Reset your password"}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {mode === "reset"
            ? "We'll email you a secure link to set a new password. The link expires shortly."
            : mode === "beta"
              ? "Free while we're in beta. In exchange, we ask for one useful note a week: a bug you hit, a rough edge, or an idea worth building."
              : mode === "request"
              ? "Billing isn't live yet, so accounts go out by invitation. Tell us who you are and what you're building — we'll email you when your access is ready."
              : mode === "create"
                ? "Use the same email you requested access with. A 4-digit PIN guards the app on this device."
                : "Your account keeps everything synced. A 4-digit PIN guards it on this device. If you ever forget the PIN, your email and password get you back in."}
        </p>

        {mode === "signin" ? (
          <div className="mt-6 rounded-sm border border-border bg-card/40 px-4 py-4">
            <p className="text-sm font-medium">New here? The beta is free.</p>
            <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
              Everything unlocked, no card, about 30 seconds to start. All we ask is one useful note a
              week while we're building — a bug, a rough edge, or an idea worth building. Every note
              keeps your seat free another 30 days.
            </p>
            <Button
              type="button"
              className="mt-3 w-full"
              onClick={() => {
                setMagicSent(false);
                setMode("beta");
              }}
            >
              Take a free beta seat
            </Button>
          </div>
        ) : null}

        {mode === "beta" ? (
          <ul className="mt-6 space-y-1.5 text-xs leading-relaxed text-muted-foreground">
            <li>· Every feature unlocked — unlimited businesses, journal, phonebook, history.</li>
            <li>· No card, no auto-charge, ever. You choose a plan or you don't.</li>
            <li>· One useful note a week — a bug, a rough edge or an idea — keeps the seat free 30 more days.</li>
            <li>· Your writing stays private and exportable, whatever happens next.</li>
          </ul>
        ) : null}

        {invite && mode !== "request" ? (
          <div className="mt-4 rounded-sm border border-dashed border-border px-4 py-3 text-sm text-muted-foreground">
            Invitation code <span className="font-mono text-foreground">{invite}</span> will be
            applied to your account.
          </div>
        ) : null}


        {mode === "request" ? (
          requested ? (
            <div className="mt-8 space-y-4">
              <div className="rounded-sm border border-border px-4 py-4 text-sm text-foreground">
                {requested}
              </div>
              <Button variant="outline" className="w-full" onClick={() => setMode("signin")}>
                Back to sign in
              </Button>
            </div>
          ) : (
            <form onSubmit={submitRequest} className="mt-8 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="req-name">Your name</Label>
                <Input
                  id="req-name"
                  required
                  maxLength={120}
                  autoComplete="name"
                  value={reqName}
                  onChange={(e) => setReqName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="req-email">Email</Label>
                <Input
                  id="req-email"
                  type="email"
                  required
                  maxLength={320}
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="req-business">Business name / what you're working on</Label>
                <Textarea
                  id="req-business"
                  required
                  maxLength={1000}
                  rows={3}
                  value={reqBusiness}
                  onChange={(e) => setReqBusiness(e.target.value)}
                />
              </div>
              <Button type="submit" className="w-full" disabled={busy}>
                {busy ? "Sending…" : "Request early access"}
              </Button>
            </form>
          )
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            {mode !== "reset" ? (
              <div className="space-y-2">
                <div className="flex items-baseline justify-between gap-3">
                  <Label htmlFor="password">Password</Label>
                  {mode === "signin" ? (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void sendMagicLink()}
                      className="text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground disabled:opacity-50"
                    >
                      Email me a sign-in link
                    </button>
                  ) : null}
                </div>
                <Input
                  id="password"
                  type="password"
                  autoComplete={mode === "create" ? "new-password" : "current-password"}
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            ) : null}
            {magicSent && mode === "signin" ? (
              <p className="rounded-sm border border-dashed border-border px-3 py-2 text-xs text-muted-foreground">
                Check your inbox for a one-time sign-in link. It works once and expires shortly —
                you can still use your password here.
              </p>
            ) : null}
            {mode === "beta" ? (
              <label className="flex cursor-pointer items-start gap-3 rounded-sm border border-border px-3 py-3 text-xs leading-relaxed text-muted-foreground">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--primary)]"
                  checked={agreeFeedback}
                  onChange={(e) => setAgreeFeedback(e.target.checked)}
                />
                <span>
                  I'll send one useful note each week while The Founders App is in beta — a bug, a
                  rough edge, or an idea worth building, not just a hello. My seat is free until the
                  app goes live, and beta testers will be offered a founders discount when billing
                  opens — discretionary, with the amount set at launch. I

                  understand the seat gives me no right to free access
                  afterwards, that beta can end with 14 days' notice, and that a seat can be closed
                  if the feedback stops. I won't be charged automatically — if I don't pick a plan I
                  keep export-only access for 30 days. See the{" "}
                  <a href="/legal#terms" className="underline">
                    beta terms
                  </a>
                  .
                </span>

              </label>
            ) : null}
            <Button type="submit" className="w-full" disabled={busy}>
              {mode === "signin"
                ? "Sign in"
                : mode === "beta"
                  ? "Take a free beta seat"
                  : mode === "create"
                    ? "Create account"
                    : "Email me a reset link"}
            </Button>
          </form>
        )}

        <button
          type="button"
          className="mt-6 w-full text-xs text-muted-foreground/80 hover:text-foreground"
          onClick={() => {
            setRequested(null);
            setMode(mode === "signin" || mode === "reset" ? "create" : "signin");
          }}
        >
          {mode === "signin" || mode === "reset"
            ? "Already invited? Create your account"
            : "Already have an account? Sign in"}
        </button>

        {mode !== "create" ? (
          <button
            type="button"
            className="mt-3 w-full text-xs text-muted-foreground/60 hover:text-foreground"
            onClick={() => setMode("create")}
          >
            Approved already? Create your account
          </button>
        ) : null}

        <button
          type="button"
          className="mt-3 w-full text-xs text-muted-foreground/80 hover:text-foreground"
          onClick={() => setMode(mode === "reset" ? "signin" : "reset")}
        >
          {mode === "reset" ? "Back to sign in" : "Forgot your password?"}
        </button>
      </div>
    </main>
  );
}
